import React from 'react';
import {
    Grid, Paper, Tabs, Tab, Box, Typography, Button, IconButton, Divider,
    Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions, Link, LinearProgress
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PaymentsIcon from '@mui/icons-material/Payments';
import PageHeader from '../../../../../components/page-header';
import { usePublicAmendmentDetailController } from './context/public-amendment-detail-controller';
import { usePublicAmendmentDetailState } from './context/public-amendment-detail-context';
import { getAmendmentTypeLabel, getAmendmentStatus } from '../../../../../model/sge/amendment/amendment-enums';
import { getAccountReportStatus } from '../../../../../model/sge/account-report/account-report-enums';
import { getLegalInstrumentTypeLabel } from '../../../../../model/sge/transfer/transfer-enums';
import { formatDocument } from '../../../../../utils/document';
import { usePublicPortalBasePath } from '../../../context/public-client-context';

function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
    return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

function EmptyRow({ colSpan, message }: { colSpan: number; message: string }) {
    return (
        <TableRow>
            <TableCell colSpan={colSpan} align="center" sx={{ py: 4, color: 'text.secondary', border: 'none' }}>
                {message}
            </TableCell>
        </TableRow>
    );
}

function InfoField({ label, value }: { label: string; value?: React.ReactNode }) {
    return (
        <Box>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="body2" sx={{ mt: 0.25 }}>{value || '-'}</Typography>
        </Box>
    );
}

type StatCardColor = 'primary' | 'warning' | 'success' | 'info';

function StatCard({ icon, label, value, caption, color = 'primary' }: {
    icon: React.ReactNode; label: string; value: string; caption?: string; color?: StatCardColor;
}) {
    return (
        <Paper
            variant="outlined"
            sx={(theme) => ({
                p: 1.5, borderRadius: 2, height: '100%',
                display: 'flex', alignItems: 'center', gap: 1.5,
                borderColor: alpha(theme.palette[color].main, 0.3),
                bgcolor: alpha(theme.palette[color].main, 0.06),
            })}
        >
            <Box sx={(theme) => ({
                width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: alpha(theme.palette[color].main, 0.15), color: theme.palette[color].main,
            })}>
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>{label}</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{value}</Typography>
                {caption && <Typography variant="caption" color="text.secondary">{caption}</Typography>}
            </Box>
        </Paper>
    );
}

export function PublicAmendmentDetailComponent() {
    const state = usePublicAmendmentDetailState();
    const ctrl = usePublicAmendmentDetailController();
    const basePath = usePublicPortalBasePath();

    const fmtCurrency = (v?: number) => v != null ? `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-';
    const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('pt-BR') : '-';

    const statusInfo = getAmendmentStatus(state.amendment?.status);

    const selectedCommittedAmount = state.selectedCommitment?.committedAmount || 0;
    const liquidatedPct = selectedCommittedAmount
        ? Math.min(100, ((state.selectedCommitment?.totalLiquidatedValue || 0) / selectedCommittedAmount) * 100)
        : 0;
    const paidPct = selectedCommittedAmount
        ? Math.min(100, ((state.selectedCommitment?.totalPaidValue || 0) / selectedCommittedAmount) * 100)
        : 0;

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title={state.amendment?.name || 'Emenda Parlamentar'}
                    subtitle={`Exercício ${state.amendment?.year || '-'} • ${getAmendmentTypeLabel(state.amendment?.type)}`}
                    actions={<Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={ctrl.navigateToList}>Voltar</Button>}
                    homeHref={basePath}
                    breadcrumbs={[{ label: 'Emendas', href: `${basePath}/emendas` }, { label: 'Detalhe' }]}
                />
            </Grid>

            {/* Parlamentar e Partido */}
            <Grid item xs={12}>
                <Paper
                    elevation={0}
                    sx={{
                        p: 2.5, borderRadius: 2, bgcolor: 'primary.main', color: 'primary.contrastText',
                        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 3,
                    }}
                >
                    <Box sx={{
                        width: 48, height: 48, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: 'rgba(255,255,255,0.15)', flexShrink: 0,
                    }}>
                        <PersonIcon />
                    </Box>
                    <Box>
                        <Typography variant="caption" sx={{ opacity: 0.85, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Parlamentar Autor
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            {state.parlamentarName || 'Não informado'}
                        </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.3)' }} />
                    <Box>
                        <Typography variant="caption" sx={{ opacity: 0.85, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Partido Político
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            {state.politicalPartyAbbreviation || 'Não informado'}
                        </Typography>
                    </Box>
                </Paper>
            </Grid>

            {/* Summary */}
            <Grid item xs={12}>
                <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column' }} elevation={3}>
                    <Grid container spacing={3}>

                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                                Informações Gerais
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                        </Grid>

                        <Grid item xs={6} md={3}>
                            <Typography variant="caption" color="text.secondary">Status</Typography>
                            <Box sx={{ mt: 0.5 }}>
                                <Chip label={statusInfo?.label || '-'} color={statusInfo?.color || 'default'} size="small" />
                            </Box>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <InfoField label="Valor Total" value={<Typography variant="body1" sx={{ fontWeight: 600 }}>{fmtCurrency(state.amendment?.totalAmount)}</Typography>} />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <InfoField label="Exercício" value={state.amendment?.year} />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <InfoField label="Tipo" value={getAmendmentTypeLabel(state.amendment?.type)} />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2, mt: 1, color: 'primary.main', fontWeight: 600 }}>
                                Classificação Orçamentária
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <InfoField label="Unidade Orçamentária" value={state.amendment?.budgetUnitName ? `${state.amendment.budgetUnitCode} - ${state.amendment.budgetUnitName}` : undefined} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <InfoField label="Programa Orçamentário" value={state.amendment?.budgetProgramName ? `${state.amendment.budgetProgramCode} - ${state.amendment.budgetProgramName}` : undefined} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <InfoField label="Ação Orçamentária" value={state.amendment?.budgetActionName ? `${state.amendment.budgetActionCode} - ${state.amendment.budgetActionName}` : undefined} />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2, mt: 1, color: 'primary.main', fontWeight: 600 }}>
                                Detalhes
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                        </Grid>

                        <Grid item xs={12}>
                            <InfoField label="Descrição do Objeto" value={state.amendment?.objectDescription} />
                        </Grid>
                        <Grid item xs={12}>
                            <InfoField label="Base Legal" value={state.amendment?.legalBasis} />
                        </Grid>
                        <Grid item xs={12}>
                            <InfoField label="Finalidade Pública" value={state.amendment?.publicPurpose} />
                        </Grid>
                        <Grid item xs={12}>
                            <InfoField label="Justificativa Institucional" value={state.amendment?.institutionalJustification} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InfoField label="Público-Alvo" value={state.amendment?.targetAudience} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InfoField label="Local de Execução" value={state.amendment?.executionLocation} />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            {/* Tabs */}
            <Grid item xs={12}>
                <Paper elevation={3}>
                    <Tabs value={state.tabIndex} onChange={ctrl.handleTabChange} variant="scrollable" scrollButtons="auto">
                        <Tab icon={<ReceiptLongIcon fontSize="small" />} iconPosition="start" label={`Empenhos (${state.commitments.length})`} />
                        <Tab icon={<FactCheckIcon fontSize="small" />} iconPosition="start" label={`Prestação de Contas (${state.accountReports.length})`} />
                        <Tab icon={<AttachFileIcon fontSize="small" />} iconPosition="start" label={`Anexos (${state.attachments.length})`} />
                        <Tab icon={<SwapHorizIcon fontSize="small" />} iconPosition="start" label={`Transferências (${state.transfers.length})`} />
                    </Tabs>
                    <Box sx={{ p: 2 }}>

                        {/* TAB 0: Commitments */}
                        <TabPanel value={state.tabIndex} index={0}>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Nº Empenho</TableCell>
                                            <TableCell>Data</TableCell>
                                            <TableCell>Fornecedor</TableCell>
                                            <TableCell align="right">Valor Empenhado</TableCell>
                                            <TableCell align="right">Valor Liquidado</TableCell>
                                            <TableCell align="right">Valor Pago</TableCell>
                                            <TableCell align="center">Detalhes</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {state.commitments.length === 0 && <EmptyRow colSpan={7} message="Nenhum empenho cadastrado" />}
                                        {state.commitments.map((c) => (
                                            <TableRow key={c.id} hover>
                                                <TableCell>{c.commitmentNumber || '-'}</TableCell>
                                                <TableCell>{fmtDate(c.commitmentDate)}</TableCell>
                                                <TableCell>{c.supplierName || '-'}</TableCell>
                                                <TableCell align="right">{fmtCurrency(c.committedAmount)}</TableCell>
                                                <TableCell align="right">{fmtCurrency(c.totalLiquidatedValue)}</TableCell>
                                                <TableCell align="right">{fmtCurrency(c.totalPaidValue)}</TableCell>
                                                <TableCell align="center">
                                                    <IconButton size="small" onClick={() => ctrl.handleViewCommitmentDetails(c)} title="Ver detalhes do empenho">
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </TabPanel>

                        {/* TAB 1: Account Reports */}
                        <TabPanel value={state.tabIndex} index={1}>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Período</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Observações</TableCell>
                                            <TableCell align="center">Detalhes</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {state.accountReports.length === 0 && <EmptyRow colSpan={4} message="Nenhuma prestação de contas cadastrada" />}
                                        {state.accountReports.map((r) => (
                                            <TableRow key={r.id} hover>
                                                <TableCell>{fmtDate(r.periodStartDate)} - {fmtDate(r.periodEndDate)}</TableCell>
                                                <TableCell>
                                                    {getAccountReportStatus(r.status) ? (
                                                        <Chip label={getAccountReportStatus(r.status)?.label} color={getAccountReportStatus(r.status)?.color} size="small" />
                                                    ) : '-'}
                                                </TableCell>
                                                <TableCell>{r.observations || '-'}</TableCell>
                                                <TableCell align="center">
                                                    <IconButton size="small" onClick={() => ctrl.handleViewAccountReportDetails(r)} title="Ver detalhes da prestação de contas">
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </TabPanel>

                        {/* TAB 2: Attachments */}
                        <TabPanel value={state.tabIndex} index={2}>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead><TableRow><TableCell>Nome</TableCell><TableCell>Tipo</TableCell><TableCell>Data</TableCell><TableCell align="right">Ações</TableCell></TableRow></TableHead>
                                    <TableBody>
                                        {state.attachments.length === 0 && <EmptyRow colSpan={4} message="Nenhum anexo disponível" />}
                                        {state.attachments.map((a) => (
                                            <TableRow key={a.id} hover>
                                                <TableCell>{a.title || a.originalFileName}</TableCell>
                                                <TableCell>{a.fileType}</TableCell>
                                                <TableCell>{fmtDate(a.createdAt)}</TableCell>
                                                <TableCell align="right">
                                                    <IconButton size="small" onClick={() => ctrl.handleDownloadAttachment(a.id, a.originalFileName || 'arquivo')}><DownloadIcon fontSize="small" /></IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </TabPanel>

                        {/* TAB 3: Transfers */}
                        <TabPanel value={state.tabIndex} index={3}>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead><TableRow><TableCell>Empresa</TableCell><TableCell>CNPJ</TableCell><TableCell>Tipo Instrumento</TableCell><TableCell>Nº Instrumento</TableCell><TableCell>Data Assinatura</TableCell><TableCell align="right">Valor</TableCell></TableRow></TableHead>
                                    <TableBody>
                                        {state.transfers.length === 0 && <EmptyRow colSpan={6} message="Nenhuma transferência cadastrada" />}
                                        {state.transfers.map((t) => (
                                            <TableRow key={t.id} hover>
                                                <TableCell>{t.companyName || '-'}</TableCell>
                                                <TableCell>{formatDocument(t.cnpj) || '-'}</TableCell>
                                                <TableCell>{getLegalInstrumentTypeLabel(t.legalInstrumentType)}</TableCell>
                                                <TableCell>{t.instrumentNumber || '-'}</TableCell>
                                                <TableCell>{fmtDate(t.signatureDate)}</TableCell>
                                                <TableCell align="right">{fmtCurrency(t.transferredAmount)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </TabPanel>
                    </Box>
                </Paper>
            </Grid>

            {/* Detalhes do Empenho */}
            <Dialog open={!!state.selectedCommitment} onClose={ctrl.handleCloseCommitmentDetails} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReceiptLongIcon color="primary" fontSize="small" />
                    Empenho {state.selectedCommitment?.commitmentNumber ? `Nº ${state.selectedCommitment.commitmentNumber}` : ''}
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                                Dados do Empenho
                            </Typography>
                            <Divider sx={{ mt: 1 }} />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <InfoField label="Fornecedor" value={state.selectedCommitment?.supplierName} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoField label="Documento" value={formatDocument(state.selectedCommitment?.supplierDocument)} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoField label="Data do Empenho" value={fmtDate(state.selectedCommitment?.commitmentDate)} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoField label="Data de Publicação" value={fmtDate(state.selectedCommitment?.publicationDate)} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoField label="Conta Bancária Utilizada" value={state.selectedCommitment?.bankAccountUsed} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoField
                                label="Link do Objeto"
                                value={state.selectedCommitment?.objectLink ? (
                                    <Link href={state.selectedCommitment.objectLink} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: 'break-all' }}>
                                        {state.selectedCommitment.objectLink}
                                    </Link>
                                ) : undefined}
                            />
                        </Grid>

                        <Grid item xs={12} sx={{ mt: 1 }}>
                            <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                                Execução Financeira
                            </Typography>
                            <Divider sx={{ mt: 1, mb: 2 }} />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <StatCard
                                icon={<AccountBalanceWalletIcon fontSize="small" />}
                                label="Valor Empenhado"
                                value={fmtCurrency(state.selectedCommitment?.committedAmount)}
                                color="primary"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <StatCard
                                icon={<FactCheckIcon fontSize="small" />}
                                label="Valor Liquidado"
                                value={fmtCurrency(state.selectedCommitment?.totalLiquidatedValue)}
                                caption={`${state.selectedCommitment?.totalLiquidations ?? 0} liquidação(ões)`}
                                color="warning"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <StatCard
                                icon={<PaymentsIcon fontSize="small" />}
                                label="Valor Pago"
                                value={fmtCurrency(state.selectedCommitment?.totalPaidValue)}
                                caption={`${state.selectedCommitment?.totalPayments ?? 0} pagamento(s)`}
                                color="success"
                            />
                        </Grid>

                        <Grid item xs={12} sx={{ mt: 1.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">Liquidado sobre o empenhado</Typography>
                                <Typography variant="caption" color="text.secondary">{liquidatedPct.toFixed(0)}%</Typography>
                            </Box>
                            <LinearProgress variant="determinate" value={liquidatedPct} color="warning" sx={{ height: 6, borderRadius: 3 }} />

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, mt: 1.5 }}>
                                <Typography variant="caption" color="text.secondary">Pago sobre o empenhado</Typography>
                                <Typography variant="caption" color="text.secondary">{paidPct.toFixed(0)}%</Typography>
                            </Box>
                            <LinearProgress variant="determinate" value={paidPct} color="success" sx={{ height: 6, borderRadius: 3 }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={ctrl.handleCloseCommitmentDetails}>Fechar</Button>
                </DialogActions>
            </Dialog>

            {/* Detalhes da Prestação de Contas */}
            <Dialog open={!!state.selectedAccountReport} onClose={ctrl.handleCloseAccountReportDetails} maxWidth="sm" fullWidth>
                <DialogTitle>Detalhes da Prestação de Contas</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <InfoField label="Período" value={`${fmtDate(state.selectedAccountReport?.periodStartDate)} - ${fmtDate(state.selectedAccountReport?.periodEndDate)}`} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">Status</Typography>
                            <Box sx={{ mt: 0.5 }}>
                                {getAccountReportStatus(state.selectedAccountReport?.status) ? (
                                    <Chip
                                        label={getAccountReportStatus(state.selectedAccountReport?.status)?.label}
                                        color={getAccountReportStatus(state.selectedAccountReport?.status)?.color}
                                        size="small"
                                    />
                                ) : '-'}
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoField label="Data de Submissão" value={fmtDate(state.selectedAccountReport?.submissionDate)} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoField label="Data de Revisão" value={fmtDate(state.selectedAccountReport?.reviewDate)} />
                        </Grid>
                        <Grid item xs={12}>
                            <InfoField label="Observações" value={state.selectedAccountReport?.observations} />
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Arquivos</Typography>
                            {state.accountReportAttachments.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">Nenhum arquivo anexado</Typography>
                            ) : (
                                <Table size="small">
                                    <TableBody>
                                        {state.accountReportAttachments.map((a) => (
                                            <TableRow key={a.id}>
                                                <TableCell>{a.title || a.originalFileName}</TableCell>
                                                <TableCell align="right">
                                                    <IconButton size="small" onClick={() => ctrl.handleDownloadAttachment(a.id, a.originalFileName || 'arquivo')}>
                                                        <DownloadIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={ctrl.handleCloseAccountReportDetails}>Fechar</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}
